import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCalendar, FaClock } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { blogAPI } from '../utils/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px var(--space-md) 80px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: var(--space-3xl);
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: var(--space-lg);
  color: var(--secondary-900);
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: var(--secondary-600);
  max-width: 600px;
  margin: 0 auto;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--space-2xl);
`;

const BlogCard = styled(motion.article)`
  background: white;
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  border: 1px solid var(--secondary-200);

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-xl);
  }
`;

const BlogImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  }
`;

const BlogContent = styled.div`
  padding: var(--space-xl);
`;

const BlogMeta = styled.div`
  display: flex;
  gap: var(--space-md);
  margin-bottom: var(--space-md);
  font-size: 0.875rem;
  color: var(--secondary-500);
  flex-wrap: wrap;
  align-items: center;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
`;

const BlogCategory = styled.span`
  background: var(--primary-100);
  color: var(--primary-700);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  font-weight: 500;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BlogTitle = styled.h2`
  margin-bottom: var(--space-md);
  font-size: 1.25rem;
  
  a {
    color: var(--secondary-900);
    text-decoration: none;
    line-height: 1.4;
    
    &:hover {
      color: var(--primary-600);
    }
  }
`;

const BlogExcerpt = styled.p`
  color: var(--secondary-600);
  line-height: 1.6;
  margin-bottom: var(--space-lg);
  font-size: 0.95rem;
`;

const ReadMore = styled(Link)`
  color: var(--primary-600);
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: 0.9rem;
  
  &:hover {
    color: var(--primary-700);
  }
`;

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await blogAPI.getAll({}, i18n.language);
        setBlogPosts(response.data.posts || []);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setBlogPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [i18n.language]);

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>{t('blog.title')}</Title>
          <Subtitle>{t('common.loading')}</Subtitle>
        </Header>
      </Container>
    );
  }

  return (
    <>
      <SEO 
        title="Blog"
        description="Insights, tips, and best practices for agile transformation and Scrum mastery. Expert articles on team development, process optimization, and agile leadership."
        keywords="Agile Blog, Scrum Master Blog, Agile Transformation, Team Development, Scrum Events, Agile Metrics, Remote Agile"
      />
      <Breadcrumbs />
      <Container>
        <Header>
          <Title>{t('blog.title')}</Title>
          <Subtitle>
            {t('blog.subtitle', 'Insights, tips, and best practices for agile transformation and Scrum mastery. Expert articles to help you and your organization succeed with agile methodologies.')}
          </Subtitle>
        </Header>

        <BlogGrid>
          {blogPosts.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 'var(--space-3xl)' }}>
              <p>{t('blog.noPosts', 'No blog posts available yet.')}</p>
            </div>
          ) : (
            blogPosts.map((post, index) => (
              <BlogCard
                key={post._id || post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <BlogImage>
                  {post.category}
                </BlogImage>
                <BlogContent>
                  <BlogMeta>
                    <BlogCategory>{post.category}</BlogCategory>
                    <MetaItem>
                      <FaCalendar />
                      {new Date(post.publishedAt).toLocaleDateString(i18n.language)}
                    </MetaItem>
                    <MetaItem>
                      <FaClock />
                      {post.readTime} {t('blog.minRead', 'min read')}
                    </MetaItem>
                  </BlogMeta>
                  <BlogTitle>
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </BlogTitle>
                  <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                  <ReadMore to={`/blog/${post.slug}`}>
                    {t('blog.readMore')} →
                  </ReadMore>
                </BlogContent>
              </BlogCard>
            ))
          )}
        </BlogGrid>
      </Container>
    </>
  );
};

export default BlogPage;